import Papa from 'papaparse'
import type { DraftState, PrimerEdit, SheetState } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { comparableFileName, sampleStemAsWritten, sampleStemOf } from '../../Util/fileNames.ts'
import { plural } from '../../Util/plural.ts'
import type { PrimerGridColumn, PrimerSide } from './primerGrid.ts'
import { PRIMER_GRID_COLUMNS, PRIMER_SIDES, isPrimer, isPrimerSide, normalizePrimer } from './primerGrid.ts'
import { hasKeyColumn, newSheetState, unmappedColumns } from './sheet/sheetMapping.ts'
import { matchSheet, namesStem } from './sheet/sheetMatching.ts'
import type { SheetMatch } from './sheet/sheetMatching.ts'
import { sheetPrimers } from './sheet/sheetImport.ts'
import { sampleDisplayName } from './sampleLabels.ts'

/** Below this share of rows matched, a keyed block (or sheet) is taken as a format the wizard does not know. */
export const LOW_MATCH_SHARE = 0.3

export type PasteTable = string[][]

/** Where a plain block lands: the group keys in the grid's current order, and the focused cell. */
export type PasteTarget = { view: readonly string[]; row: number; column: PrimerGridColumn }

/** Why a block cannot be pasted, as a category. */
export type PasteRefusal =
  | 'empty'
  | 'no_matching_rows'
  | 'ids_dont_match_files'
  | 'no_primers'
  | 'no_key_column'
  | 'not_primers'
  | 'nothing_landed'

export type PrimerPastePlan =
  | {
      kind: 'keyed'
      edits: PrimerEdit[]
      primers: number
      matched: number
      total: number
      via: SheetMatch['via'][number]
      invalid: string[]
      /** The block as a sample sheet, when it brings metadata columns. */
      sheet?: SheetState
      lowMatch: boolean
      headers: string[]
    }
  | {
      kind: 'positional'
      edits: PrimerEdit[]
      primers: number
      /** 1-based row of the current view the block starts at. */
      startRow: number
      /** Rows in the block, header excluded. */
      rows: number
      invalid: string[]
      rowsLeftOut: number
      cellsLeftOut: number
      headerSkipped: boolean
    }
  | { kind: 'unreadable'; reason: string; refusal: PasteRefusal; rows: number; headers: string[]; sheetLike: boolean }

/** A pasted block as spreadsheets put it on the clipboard: tab separated, quoted where a cell holds a line break. */
export const parsePastedText = (text: string): PasteTable => {
  const delimiter = text.includes('\t') ? '\t' : ''
  const parsed = Papa.parse<string[]>(text.replace(/\r\n?/g, '\n'), { delimiter, skipEmptyLines: 'greedy' })
  return cleanTable(parsed.data)
}

export const cleanTable = (table: readonly (readonly string[])[]): PasteTable =>
  table.map((row) => row.map((cell) => (cell ?? '').trim())).filter((row) => row.some((cell) => cell !== ''))

const fastqNames = (draft: DraftState) =>
  Object.values(draft.files)
    .filter((file) => file.kind === 'fastq')
    .map((file) => file.name)

const namesASample = (draft: DraftState) => {
  const names = fastqNames(draft)
  const files = new Set(names.map(comparableFileName))
  const stems = names.map(sampleStemOf)
  return (cell: string) => files.has(comparableFileName(cell)) || stems.some((stem) => namesStem(cell, stem))
}

const uniqueHeaders = (cells: readonly string[]): string[] => {
  const seen = new Map<string, number>()
  return cells.map((cell, index) => {
    const base = cell || `Column ${index + 1}`
    const count = (seen.get(base) ?? 0) + 1
    seen.set(base, count)
    return count === 1 ? base : `${base} (${count})`
  })
}

const hashOf = (text: string): string => {
  let hash = 5381
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(36)
}

const editsFrom = (byKey: Map<string, PrimerEdit>): PrimerEdit[] => [...byKey.values()]

const countPrimers = (edits: readonly PrimerEdit[]) => edits.reduce((sum, edit) => sum + PRIMER_SIDES.filter((side) => edit[side]).length, 0)

const mostCommon = <T>(values: readonly T[]): T | undefined => {
  const counts = new Map<T, number>()
  values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1))
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0]
}

/* sequencing-style IDs: long, with digits and separators, neither a primer nor a word */
const looksLikeId = (value: string) => value.length >= 8 && /\d/.test(value) && /[_-]/.test(value) && !/\s/.test(value) && !isPrimer(value)

const tokensOf = (value: string) => value.toLowerCase().split(/[_\-.]+/).filter((token) => token !== '')

/* parts shared with the file names (`FC25`, `pCO1`, a well) beat a merely ID-shaped column such as a dated customer name */
const idScore = (values: readonly string[], fileTokens: ReadonlySet<string>) => {
  const tokens = values.flatMap(tokensOf)
  return { shared: tokens.filter((token) => fileTokens.has(token)).length / values.length, parts: tokens.length / values.length }
}

/** The most sequencing-like column of IDs unique per row, or none; ties go to the earlier column. */
const idLikeColumn = (sheet: SheetState, fileStems: readonly string[]): { header: string; values: string[] } | undefined => {
  const fileTokens = new Set(fileStems.flatMap(tokensOf))
  const eligible = sheet.headers.flatMap((header) => {
    const values = sheet.rows.map((row) => (row[header] ?? '').trim()).filter((value) => value !== '')
    const qualifies =
      values.length > 0 &&
      values.length >= 0.8 * sheet.rows.length &&
      new Set(values).size === values.length &&
      values.filter(looksLikeId).length >= 0.8 * values.length
    return qualifies ? [{ header, values, ...idScore(values, fileTokens) }] : []
  })
  return eligible.reduce<(typeof eligible)[number] | undefined>(
    (best, column) => (!best || column.shared > best.shared || (column.shared === best.shared && column.parts > best.parts) ? column : best),
    undefined
  )
}

const EXAMPLE_LENGTH = 40

const example = (text: string) => (text.length > EXAMPLE_LENGTH ? `${text.slice(0, EXAMPLE_LENGTH - 1)}…` : text)

/** Why nothing matched, with one ID and one file stem side by side, when the table holds sequencing-style IDs none of which names a sample. */
const idMismatch = (draft: DraftState, sheet: SheetState): string | undefined => {
  const stems = draft.fileOrder.flatMap((key) => (draft.files[key]?.kind === 'fastq' ? [sampleStemAsWritten(draft.files[key].name)] : []))
  const column = stems.length > 0 ? idLikeColumn(sheet, stems) : undefined
  if (!column || column.values.some(namesASample(draft))) {
    return undefined
  }
  return `None of the table's IDs match your file names. The table's IDs look like “${example(column.values[0])}”, your files like “${example(stems[0])}”. Rename the files, or add a column with the file names, or select the first sample row in the grid and paste only the primer columns.`
}

const keyedPlan = (draft: DraftState, sheet: SheetState, hasHeader: boolean): PrimerPastePlan => {
  const match = matchSheet(sheet, draft)
  if (Object.keys(match.byGroup).length === 0) {
    const mismatch = idMismatch(draft, sheet)
    return {
      kind: 'unreadable',
      reason: mismatch ?? 'No row names one of your samples or files.',
      refusal: mismatch ? 'ids_dont_match_files' : 'no_matching_rows',
      rows: sheet.rows.length,
      headers: sheet.headers,
      sheetLike: true
    }
  }
  const byKey = new Map<string, PrimerEdit>()
  const invalid: string[] = []
  for (const [groupKey, rowIndex] of Object.entries(match.byGroup)) {
    const primers = sheetPrimers(sheet.rows[rowIndex], sheet.mapping)
    for (const side of PRIMER_SIDES) {
      const value = primers[side]
      if (!value) {
        continue
      }
      if (!isPrimer(value)) {
        invalid.push(`${sampleDisplayName(draft, groupKey)} ${side}: “${value}”`)
        continue
      }
      byKey.set(groupKey, { ...(byKey.get(groupKey) ?? { key: groupKey }), [side]: normalizePrimer(value) })
    }
  }
  const metadata =
    hasHeader && (Object.keys(sheet.mapping.metadata).length > 0 || unmappedColumns(sheet.headers, sheet.mapping).length > 0)
  const edits = editsFrom(byKey)
  if (edits.length === 0 && invalid.length === 0 && !metadata) {
    return {
      kind: 'unreadable',
      reason: 'The pasted rows name your samples but carry no primers.',
      refusal: 'no_primers',
      rows: sheet.rows.length,
      headers: sheet.headers,
      sheetLike: false
    }
  }
  const matched = Object.keys(match.byGroup).length
  return {
    kind: 'keyed',
    edits,
    primers: countPrimers(edits),
    matched,
    total: sheet.rows.length,
    via: mostCommon(Object.values(match.via)) ?? 'sample name',
    invalid,
    sheet: metadata ? sheet : undefined,
    lowMatch: sheet.rows.length > 0 && matched / sheet.rows.length < LOW_MATCH_SHARE,
    headers: sheet.headers
  }
}

const positionalPlan = (body: PasteTable, target: PasteTarget, headerSkipped: boolean): PrimerPastePlan => {
  const start: PrimerSide = isPrimerSide(target.column) ? target.column : 'forward'
  const startColumn = PRIMER_GRID_COLUMNS.indexOf(start)
  const byKey = new Map<string, PrimerEdit>()
  const invalid: string[] = []
  let rowsLeftOut = 0
  let cellsLeftOut = 0
  body.forEach((cells, offset) => {
    const viewRow = target.row + offset
    const key = target.view[viewRow]
    if (key === undefined) {
      rowsLeftOut++
      return
    }
    cells.forEach((cell, columnOffset) => {
      const column = PRIMER_GRID_COLUMNS[startColumn + columnOffset]
      if (cell === '') {
        return
      }
      if (!column || !isPrimerSide(column)) {
        cellsLeftOut++
        return
      }
      if (!isPrimer(cell)) {
        invalid.push(`row ${viewRow + 1} ${column}: “${cell}”`)
        return
      }
      byKey.set(key, { ...(byKey.get(key) ?? { key }), [column]: normalizePrimer(cell) })
    })
  })
  const edits = editsFrom(byKey)
  if (edits.length === 0) {
    return {
      kind: 'unreadable',
      reason:
        invalid.length > 0
          ? 'The pasted cells are not primers, and no column names your samples or files.'
          : 'Nothing landed on a primer cell.',
      refusal: invalid.length > 0 ? 'not_primers' : 'nothing_landed',
      rows: body.length,
      headers: [],
      sheetLike: invalid.length > 0 && body.some((cells) => cells.length > 1)
    }
  }
  return {
    kind: 'positional',
    edits,
    primers: countPrimers(edits),
    startRow: target.row + 1,
    rows: body.length,
    invalid,
    rowsLeftOut,
    cellsLeftOut,
    headerSkipped
  }
}

/**
 * What a pasted block would do. A block naming the samples (by sample id, sample name or file name, with a
 * header row or recognisable by its values) is matched like a sample sheet, order-independent, and brings
 * its metadata columns along. Any other block of primers is pasted by position into the grid's current
 * view from the focused cell, never past its last row.
 */
export const planPrimerPaste = (draft: DraftState, table: readonly (readonly string[])[], target: PasteTarget): PrimerPastePlan => {
  const cleaned = cleanTable(table)
  if (cleaned.length === 0) {
    return { kind: 'unreadable', reason: 'Nothing to paste.', refusal: 'empty', rows: 0, headers: [], sheetLike: false }
  }
  const namesSample = namesASample(draft)
  const [first] = cleaned
  const hasHeader = cleaned.length >= 2 && first.every((cell) => cell === '' || (!isPrimer(cell) && !namesSample(cell)))
  const width = Math.max(...cleaned.map((row) => row.length))
  const headers = hasHeader ? uniqueHeaders(first) : uniqueHeaders(Array.from({ length: width }, () => ''))
  const body = hasHeader ? cleaned.slice(1) : cleaned
  const rows = body.map((cells) => Object.fromEntries(headers.map((header, i) => [header, cells[i] ?? ''])))
  const text = cleaned.map((row) => row.join('\t')).join('\n')
  const sheet = newSheetState(
    { key: `paste-${hashOf(text)}`, name: 'Pasted rows' },
    { headers, rows, notes: [] },
    { fileNames: fastqNames(draft) }
  )
  if (hasKeyColumn(sheet.mapping)) {
    return keyedPlan(draft, sheet, hasHeader)
  }
  if (hasHeader && !body.every((cells) => cells.every((cell) => cell === '' || isPrimer(cell)))) {
    const mismatch = idMismatch(draft, sheet)
    return {
      kind: 'unreadable',
      reason: mismatch ?? 'No column names your samples or files.',
      refusal: mismatch ? 'ids_dont_match_files' : 'no_key_column',
      rows: body.length,
      headers,
      sheetLike: true
    }
  }
  return positionalPlan(body, target, hasHeader)
}

const VIA_TEXT: Record<SheetMatch['via'][number], string> = { file: 'file name', 'sample id': 'sample id', 'sample name': 'name' }

/** The preview's headline and notes, before anything is applied. */
export const pastePreview = (plan: PrimerPastePlan, currentSheet?: string): { headline: string; notes: string[] } => {
  switch (plan.kind) {
    case 'keyed': {
      const notes = [
        plan.primers > 0 ? `${plural(plan.primers, 'primer')} to apply` : 'no primers in these rows',
        plan.invalid.length > 0 && `${plural(plan.invalid.length, 'cell')} left out, not a primer: ${plan.invalid.join(', ')}`,
        plan.sheet &&
          `its other columns are imported as sample metadata${currentSheet ? `, replacing the sample sheet ${currentSheet}` : ''}`
      ]
      return {
        headline: `${plan.matched} matched by ${VIA_TEXT[plan.via]} · ${plan.total - plan.matched} not matched`,
        notes: notes.filter((note): note is string => !!note)
      }
    }
    case 'positional': {
      const notes = [
        plan.headerSkipped && 'header row skipped',
        plan.rowsLeftOut > 0 && `${plural(plan.rowsLeftOut, 'row')} below the last sample left out`,
        plan.cellsLeftOut > 0 && `${plural(plan.cellsLeftOut, 'cell')} outside the primer columns left out`,
        plan.invalid.length > 0 && `${plural(plan.invalid.length, 'cell')} left out, not a primer: ${plan.invalid.join(', ')}`
      ]
      return {
        headline: `${plural(plan.primers, 'primer')} pasted by position from row ${plan.startRow}`,
        notes: notes.filter((note): note is string => !!note)
      }
    }
    case 'unreadable':
      return { headline: plan.reason, notes: [] }
  }
}
